import { executeNavigationAction, getNavigationAction } from '@/lib/navigation';
import { IPMA_SOURCE_ACTION } from '@/lib/sources';
import { cn } from '@/lib/utils';
import { MessageContext } from 'contexts/MessageContext';
import { ArrowRight } from 'lucide-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { toast } from 'sonner';

import {
  ChainlitContext,
  type IAction,
  sessionIdState
} from '@chainlit/react-client';

import Icon from '@/components/Icon';
import { Loader } from '@/components/Loader';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface ActionProps {
  action: IAction;
}

const CERTIFICATION_FINDER_ACTION = 'certification_finder_answer';

const ActionButton = ({ action }: ActionProps) => {
  const { loading, askUser } = useContext(MessageContext);
  const apiClient = useContext(ChainlitContext);
  const sessionId = useRecoilValue(sessionIdState);
  const [isRunning, setIsRunning] = useState(false);
  const navigationAction = getNavigationAction(action);
  const isFinderAction = action.name === CERTIFICATION_FINDER_ACTION;

  const content = useMemo(() => {
    return action.icon
      ? action.label
      : action.label
      ? action.label
      : action.name;
  }, [action]);

  const icon = useMemo(() => {
    if (isRunning) return <Loader />;
    if (action.icon) return <Icon name={action.icon as any} />;
    return null;
  }, [action, isRunning]);

  const handleClick = useCallback(async () => {
    try {
      setIsRunning(true);
      if (navigationAction) {
        await executeNavigationAction(navigationAction);
        return;
      }
      await apiClient.callAction(action, sessionId);
    } catch (err) {
      toast.error(String(err));
    } finally {
      setIsRunning(false);
    }
  }, [action, sessionId, apiClient, navigationAction]);

  const isAskingAction = askUser?.spec.type === 'action';
  const ignore = isAskingAction && askUser?.spec.keys?.includes(action.id);

  if (ignore) return null;
  if (navigationAction?.behavior === 'auto') return null;
  if (action.name === IPMA_SOURCE_ACTION) return null;

  const button = (
    <Button
      id={action.id}
      onClick={handleClick}
      size="sm"
      variant={navigationAction || isFinderAction ? 'outline' : 'ghost'}
      className={cn(
        'transition-colors duration-150',
        navigationAction
          ? 'navigation-action-button h-8 rounded-lg border-border/80 bg-background px-3 text-xs font-semibold text-foreground shadow-none hover:border-primary/35 hover:bg-primary/5 hover:text-primary'
          : isFinderAction
          ? 'h-9 rounded-lg border-border/80 bg-background px-3 text-xs font-semibold text-foreground shadow-none hover:border-primary/35 hover:bg-primary/5 hover:text-primary'
          : 'text-muted-foreground'
      )}
      disabled={loading || isRunning}
    >
      {navigationAction ? (
        <>
          {content}
          <ArrowRight className="!size-3.5" aria-hidden="true" />
        </>
      ) : (
        <>
          {icon}
          {content}
        </>
      )}
    </Button>
  );

  if (action.tooltip) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            <p>{action.tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  } else {
    return button;
  }
};

export { ActionButton };
